<template>
  <div>
    <b-card>
      <template slot="header">
        <h4 class="mt-2 custom-card-header-text">Approve Opportunities</h4>
        <ul class="list-inline float-right header_table">
          <li class="list-inline-item">
            <button
              type="button"
              aria-label="Export"
              :disabled="exporting"
              @click="exportOpportunities()"
              title="Download opportunities data"
              class="btn custom-card-header-filter btn-dropbox tab-focus"
            >
              <i :class="exporting ? 'fa fa-spinner fa-spin' : 'icon-cloud-download'"></i>
            </button>
          </li>
        </ul>
      </template>
      <b-card-body>
        <div
          v-if="loading"
          class="text-center py-5"
          role="status"
          aria-live="polite"
          aria-busy="true"
        >
          <i class="fa fa-spinner fa-spin fa-2x mb-3 d-block" aria-hidden="true"></i>
          <p class="mb-0 text-muted">Loading opportunities…</p>
        </div>
        <template v-else>
          <search-section
            :show-approval-dropdown="false"
            search-placeholder="Search Opportunities..."
            @search="handleSearch"
            @limit-change="handleLimitChange"
          >
            <template slot="before-records">
              <b-form-group
                label="Status:"
                label-for="status-filter"
                class="mb-0 limit-group mr-3"
              >
                <b-form-select
                  id="status-filter"
                  v-model="selectedStatus"
                  :options="statusOptions"
                  class="custom-limit-select"
                />
              </b-form-group>
            </template>
          </search-section>

          <v-client-table
            ref="opportunitiesTable"
            :data="filteredOpportunities"
            :columns="columns"
            :options="tableOptions"
            id="opportunitiesApprovalTable"
          >
            <a
              slot="title"
              slot-scope="props"
              :href="opportunityDetailHref(props.row._id)"
              target="_blank"
              rel="noopener"
              class="display-link"
            >
              {{ props.row.title || 'Untitled' }}
            </a>
            <span slot="submittedDate" slot-scope="props" v-if="props.row.submittedDate">
              {{ props.row.submittedDate | momentDate }}
            </span>
            <span slot="submittedDate" v-else>N/A</span>
            <span slot="status" slot-scope="props">
              <b-badge :variant="statusVariant(props.row.status)" class="text-capitalize">
                {{ statusLabel(props.row.status) }}
              </b-badge>
            </span>
            <span slot="action" slot-scope="props">
              <b-dropdown
                right
                boundary="viewport"
                variant="btn btn-dropbox btn-sm tab-focus"
                class="action-status-dropdown"
                v-b-tooltip.hover.bottom
                title="Take action"
              >
                <template slot="button-content">
                  <i class="icon-settings"></i>
                </template>
                <b-dropdown-item
                  :href="opportunityDetailHref(props.row._id)"
                  target="_blank"
                  rel="noopener"
                >
                  View Opportunity
                </b-dropdown-item>
                <b-dropdown-item
                  v-for="a in rowStatusActions(props.row.status)"
                  :key="a.value"
                  @click="applyStatusChange(props.row, a.value)"
                >
                  {{ a.label }} Opportunity
                </b-dropdown-item>
              </b-dropdown>
            </span>
          </v-client-table>
        </template>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import axios from 'axios'
import SearchSection from '@/views/engagement/components/searchSection.vue'
import {
  CORE_API_URL,
  STATUS_ACTIONS,
  coreApiPageToDashboardList,
  opportunityToReportRow,
  REPORT_DATE_FIELDS,
} from './opportunityAdapter'
import { exportCSV } from '@/assets/js/common'

// Statuses that are surfaced as "needs approval" by default (matches the queue's purpose).
const PRE_APPROVAL_STATUSES = ['in_review', 'incomplete', 'quarantined']

export default {
  name: 'OpportunitiesApproval',
  components: {
    SearchSection,
  },
  data() {
    return {
      loading: true,
      exporting: false,
      opportunities: [],
      searchText: '',
      selectedStatus: 'all',
      limit: 10,
      statusOptions: [
        { value: 'all', text: 'All' },
        { value: 'in_review', text: 'In Review' },
        { value: 'incomplete', text: 'Incomplete' },
        { value: 'quarantined', text: 'Quarantined' },
      ],
      columns: ['submittedDate', 'title', 'company', 'communityName', 'location', 'status', 'action'],
      tableOptions: {
        headings: {
          submittedDate: 'Date',
          title: 'Opportunity',
          company: 'Company',
          communityName: 'Community',
          location: 'Location',
          status: 'Status',
          action: 'Action',
        },
        sortable: ['submittedDate', 'title', 'company', 'communityName', 'location', 'status'],
        orderBy: { column: 'submittedDate', ascending: false },
        sortIcon: { base: 'fa', up: 'fa-sort-asc', down: 'fa-sort-desc', is: 'fa-sort' },
        pagination: { chunk: 10, nav: 'fixed' },
        perPage: 10,
        // Hide the built-in filter/limit UI — SearchSection is the single source of truth for both.
        filterable: false,
        perPageDropdown: [],
        columnsClasses: {
          status: 'text-center col-status',
          action: 'text-center col-action',
        },
      },
    }
  },
  computed: {
    // Applied filters: status dropdown + search box. Runs in-memory over the loaded queue.
    filteredOpportunities() {
      const q = (this.searchText || '').trim().toLowerCase()
      return this.opportunities.filter((o) => {
        if (this.selectedStatus !== 'all' && o.status !== this.selectedStatus) return false
        if (!q) return true
        return (
          (o.title || '').toLowerCase().includes(q) ||
          (o.company || '').toLowerCase().includes(q) ||
          (o.communityName || '').toLowerCase().includes(q)
        )
      })
    },
  },
  watch: {
    limit(newLimit) {
      if (this.tableOptions.perPage !== newLimit) {
        this.tableOptions = { ...this.tableOptions, perPage: newLimit }
      }
    },
  },
  async created() {
    // Recruiting-only view: redirect direct URLs to the dashboard when the
    // community has Recruiting off, before the API 403 gets read as a logout.
    const communityFromContext =
      this.$route.params.community_id ||
      this.$route.query.community_id ||
      localStorage.getItem('user_modal_option')
    if (communityFromContext && communityFromContext !== 'all') {
      const enabled = await this.isRecruitingEnabled(communityFromContext)
      if (!enabled) {
        this.$router.replace('/dashboard')
        return
      }
    }
    await this.loadOpportunities()
  },
  methods: {
    // True when the community has the Recruiting module enabled in Hermes.
    async isRecruitingEnabled(communityId) {
      try {
        const { data } = await axios.get(
          `${CORE_API_URL}/api/recruiting/${communityId}`,
        )
        return data && data.enabled === true
      } catch (err) {
        return false
      }
    },
    async loadOpportunities() {
      this.loading = true
      try {
        const authToken = localStorage.getItem('id_token')
        // Scope the queue to the selected community (same pattern as OpportunityList).
        // The community switcher limits non-SuperAdmins to their own communities
        const selectedCommunityId =
          this.$route.params.community_id ||
          this.$route.query.community_id ||
          localStorage.getItem('user_modal_option')
        const communityName = localStorage.getItem('community_name')
        const communityScope =
          selectedCommunityId && selectedCommunityId !== 'all' && communityName
            ? { community: communityName }
            : {}
        // Fetch each pre-approval status in parallel; DTO only accepts single status.
        const responses = await Promise.all(
          PRE_APPROVAL_STATUSES.map((status) =>
            axios.get(`${CORE_API_URL}/api/opportunities`, {
              params: { status, limit: 500, offset: 0, ...communityScope },
              headers: authToken ? { Authorization: `Bearer ${authToken}` } : {},
            }),
          ),
        )
        const merged = responses.flatMap((res) => {
          const { opportunities } = coreApiPageToDashboardList(res.data)
          return opportunities
        })
        // Adapter surfaces `communityId` as either a string or the populated object;
        // extract the human-readable name for the Community column.
        this.opportunities = merged.map((o) => ({
          ...o,
          communityName:
            o.communityId && typeof o.communityId === 'object'
              ? o.communityId.name || ''
              : '',
        }))
      } catch (err) {
        if (err.response && (err.response.status === 401 || err.response.status === 403)) {
          localStorage.removeItem('authUser')
          this.$router.push('/login')
          return
        }
        this.opportunities = []
      } finally {
        this.loading = false
      }
    },
    rowStatusActions(currentStatus) {
      return STATUS_ACTIONS.filter((a) => a.value !== currentStatus)
    },
    async applyStatusChange(row, newStatus) {
      if (!row || !newStatus || row.status === newStatus) return
      try {
        const authToken = localStorage.getItem('id_token')
        await axios.patch(
          `${CORE_API_URL}/api/opportunities/${row._id}`,
          { status: newStatus },
          { headers: authToken ? { Authorization: `Bearer ${authToken}` } : {} },
        )
        await this.loadOpportunities()
      } catch (err) {
        if (err.response && (err.response.status === 401 || err.response.status === 403)) {
          localStorage.removeItem('authUser')
          this.$router.push('/login')
          return
        }
        alert("We couldn't update this opportunity's status. Please try again.")
      }
    },
    opportunityDetailHref(id) {
      // Detail opens in a new tab (SCRUM-719); resolve() keeps the router base.
      return this.$router.resolve(`/engagement/opportunities/${id}`).href
    },
    statusLabel(status) {
      const labels = {
        in_review: 'In Review',
        active: 'Approved',
        closed: 'Closed',
        rejected: 'Rejected',
        incomplete: 'Incomplete',
        quarantined: 'Quarantined',
      }
      return labels[status] || status || 'N/A'
    },
    // Mirror OpportunityList's badge variants so both screens share the same visual language.
    statusVariant(status) {
      const variants = {
        active: 'success',
        in_review: 'warning',
        closed: 'secondary',
        rejected: 'danger',
        incomplete: 'info',
        quarantined: 'dark',
      }
      return variants[status] || 'secondary'
    },
    // Export the queue as CSV. The pre-approval set is already in memory, so this
    // exports exactly what the current status/search filters show.
    async exportOpportunities() {
      if (this.exporting) return
      this.exporting = true
      try {
        const rows = this.filteredOpportunities
        if (!rows.length) {
          alert('There are no opportunities matching the current filters to export.')
          return
        }
        await exportCSV(rows.map(opportunityToReportRow), 'opportunities', [], REPORT_DATE_FIELDS)
      } catch (err) {
        alert("We couldn't export the opportunities. Please try again in a moment.")
      } finally {
        this.exporting = false
      }
    },
    handleSearch(text) {
      this.searchText = text
    },
    handleLimitChange(newLimit) {
      this.limit = newLimit
    },
  },
}
</script>

<style scoped>
/* Column widths — keep Status/Action tight, matching OpportunityList. */
::v-deep .col-status { width: 140px; min-width: 140px; }
::v-deep .col-action { width: 90px; min-width: 90px; }

/* Base color inherits from the row so Opportunity title matches every other
   column; `.display-link:hover` in main.css handles the underline + blue tint. */
.display-link {
  color: inherit;
  cursor: pointer;
}
</style>
